import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Modal from '../components/Modal';

interface AppraisalTemplate {
  id: string;
  code: string;
  template_name: string;
  is_active: boolean;
}

interface Parameter {
  id: string;
  parameter_type: string;
  parameter_name: string;
  weightage: number;
}

export default function AppraisalTemplates() {
  const [templates, setTemplates] = useState<AppraisalTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [kraTarget, setKraTarget] = useState<AppraisalTemplate | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    templateName: '',
    description: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/pas/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch appraisal templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/pas/templates', formData);
      setFormData({ code: '', templateName: '', description: '' });
      setShowForm(false);
      fetchTemplates();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create appraisal template');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Appraisal Templates</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Create Template'}</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Template Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
              <Input
                placeholder="Template Name"
                value={formData.templateName}
                onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                required
              />
              <textarea
                placeholder="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="px-4 py-2 border rounded-lg col-span-2"
                rows={3}
              />
            </div>
            <Button type="submit" className="w-full">
              Create Template
            </Button>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-center text-gray-600">Loading appraisal templates...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-lg mb-2">{template.template_name}</h3>
              <p className="text-sm text-gray-600 mb-3">Code: {template.code}</p>
              <div className="flex items-center justify-between">
                <span
                  className={`px-2 py-1 rounded text-xs font-semibold ${template.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                >
                  {template.is_active ? 'Active' : 'Inactive'}
                </span>
                <button onClick={() => setKraTarget(template)} className="text-blue-600 hover:underline text-sm">
                  Manage KRAs
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {kraTarget && <ParametersModal template={kraTarget} onClose={() => setKraTarget(null)} />}
    </div>
  );
}

function ParametersModal({ template, onClose }: { template: AppraisalTemplate; onClose: () => void }) {
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [totalWeight, setTotalWeight] = useState(0);
  const [form, setForm] = useState({ parameterType: 'KRA', parameterName: '', weightage: 10 });

  useEffect(() => {
    loadParameters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadParameters = async () => {
    const res = await api.get(`/pas/templates/${template.id}/parameters`);
    setParameters(res.data.parameters || []);
    setTotalWeight(res.data.totalWeight || 0);
  };

  const addParameter = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/pas/templates/${template.id}/parameters`, form);
      setForm({ parameterType: 'KRA', parameterName: '', weightage: 10 });
      loadParameters();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to add KRA');
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={`${template.template_name} — KRAs`}>
      <form onSubmit={addParameter} className="grid grid-cols-3 gap-3 mb-4">
        <Select
          value={form.parameterType}
          onChange={(e) => setForm({ ...form, parameterType: e.target.value })}
          options={[
            { value: 'KRA', label: 'KRA' },
            { value: 'COMPETENCY', label: 'Competency' },
            { value: 'GOAL', label: 'Goal' },
            { value: 'BEHAVIORAL', label: 'Behavioral' },
          ]}
        />
        <Input
          placeholder="Name"
          value={form.parameterName}
          onChange={(e) => setForm({ ...form, parameterName: e.target.value })}
          required
        />
        <Input
          type="number"
          placeholder="Weight %"
          value={form.weightage}
          onChange={(e) => setForm({ ...form, weightage: Number(e.target.value) })}
        />
        <Button type="submit" className="col-span-3">
          Add
        </Button>
      </form>

      <div
        className={`text-sm mb-3 px-3 py-2 rounded-lg ${
          totalWeight === 100 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
        }`}
      >
        Total weight: {totalWeight}% {totalWeight !== 100 && '— should sum to 100% for accurate scoring'}
      </div>

      <ul className="divide-y">
        {parameters.map((p) => (
          <li key={p.id} className="py-2 flex justify-between text-sm">
            <span>
              {p.parameter_name} <span className="text-gray-400">({p.parameter_type})</span>
            </span>
            <span className="font-semibold">{p.weightage}%</span>
          </li>
        ))}
        {parameters.length === 0 && <p className="text-gray-400 text-sm py-2">No KRAs yet</p>}
      </ul>
    </Modal>
  );
}
